/**
 * store/deck.ts — the two decks: which record is on each, which hand is on it, its tempo, its
 * KEYLOCK and CUE, the track on it — and which of the two is active.
 *
 * Task 21 made the slice for the one deck of «Сейчас играет»; task 55 makes it two equal decks,
 * A and B (`docs/superpowers/specs/2026-10-05-pult-design.md`, «Две равные деки»). Its own slice:
 * nothing that draws the list or the player is subscribed to it.
 *
 * ## The turntable is one deck to its listener
 *
 * The pult sets each deck apart. The turntable of «Сейчас играет» shows the active deck, and its
 * tempo slider and 33/45 set **both** ({@link EVERY_DECK}): a listener who never opens the pult
 * set one tempo for the music, as before task 55, and an automatic transition onto the other deck
 * must not change it.
 *
 * ## Two decks, and the active one
 *
 * `decks.A` and `decks.B` are the pult's two decks, and the player's two elements are theirs:
 * deck A is the element a track plays on first, deck B the other (`engine/element.ts`). The
 * **active** deck (`active`) is the one whose track the player slice holds — the one the
 * turntable of «Сейчас играет», the lock screen and the slider look at. The player says which
 * ({@link setActiveDeck}): since the mixer of task 56, the deck heard louder.
 *
 * The top-level `size`, `pitch` and `hand` are **the active deck's**, kept in step by every write
 * here: the turntable and its tonearm read the deck they show without knowing there are two. The
 * scratch knows (task 57): it reads `decks[side]`. A function here that takes no deck acts on the
 * active one.
 *
 * ## What each deck holds
 *
 * - **The record** (`size`, task 21), remembered on the phone per deck ({@link recordKey}): a
 *   first launch, a value nobody wrote, a phone that refuses storage — 45, without an error.
 * - **The hand** (`hand`): one at a time on a deck (`spec.md` «Пластинка») — a finger on the
 *   platter or on the tonearm; while one is there, 33/45 does not switch. Read with `getState()`
 *   in handlers only.
 * - **The tempo** (`pitch`, tasks 23 and 48), in percent, −8 … +8, clicking into zero near zero
 *   ({@link PITCH_DETENT}); remembered per deck ({@link pitchKey}). A value the slider could not
 *   have written is broken, and a broken one is 0.
 * - **KEYLOCK** (`keylock`): the tone held at every speed of the deck's own (`engine/speed.ts`).
 *   Not remembered: a launch starts with the record's tone, as a deck does.
 * - **CUE** (`cueS`): the second the deck returns to; not remembered, not on the server (design,
 *   «Вне рамок»).
 * - **The track** (`track`) and where it is (`positionS`, `durationS`, `playing`) — written by the
 *   player, read by the pult's screen and keys.
 * - **The face** (`face`, 2026-11-04): what the middle of the record shows, a label or the track's
 *   cover (`lib/labels.ts`). The active deck's is the face of the turntable of «Сейчас играет» too
 *   (it shows that deck's size and pitch, so its face goes with them), which draws it as it opens
 *   and at each tap; the pult's platter shows it and draws it at its own tap — the owner tapped
 *   the pult's record expecting what the turntable's does. The other deck's is drawn as the pult
 *   opens; the active one keeps the face the turntable showed. Not remembered: a face is drawn
 *   anew at every opening, as before.
 *
 * ## The keys of one deck before two (task 55)
 *
 * Before task 55 the phone kept one record and one tempo, under {@link LEGACY_RECORD_KEY} and
 * {@link LEGACY_PITCH_KEY}. A phone that has them and not a deck's own keys reads them once as
 * that deck's — both decks', for the turntable's reason above — and copies them under the deck's
 * keys; the old ones are not written any more.
 */

import { create } from 'zustand';

import type { TrackItem } from '../lib/api';
import type { RecordFace } from '../lib/labels';
import type { DeckSide } from '../lib/pult-layout';
import { DEFAULT_RECORD, asRecordSize } from '../lib/records';
import type { RecordSize } from '../lib/records';

export type { DeckSide } from '../lib/pult-layout';

/** Where a deck's record is remembered. */
export function recordKey(side: DeckSide): string {
  return `fdm:record:${side}`;
}

/** Task 48: where a deck's tempo is remembered, in percent as the slider holds it. */
export function pitchKey(side: DeckSide): string {
  return `fdm:pitch:${side}`;
}

/** Before task 55: the one deck's record and tempo — read once as both decks'. */
export const LEGACY_RECORD_KEY = 'fdm:record';
export const LEGACY_PITCH_KEY = 'fdm:pitch';

/** The turntable's way of naming its deck: both of them — see the header. */
export const EVERY_DECK = 'both';

/** A deck, or — the turntable's — both. */
export type DeckTarget = DeckSide | typeof EVERY_DECK;

/** What holds a deck now. */
export type DeckHand = 'none' | 'record' | 'arm';

/** The pitch slider's reach, in percent, either way. */
export const PITCH_RANGE = 8;

/** Within this of zero, in percent, the slider is at zero. */
export const PITCH_DETENT = 0.3;

export interface DeckSideState {
  readonly size: RecordSize;
  readonly hand: DeckHand;
  /** The tempo, in percent. */
  readonly pitch: number;
  readonly keylock: boolean;
  /** CUE: the second the deck returns to, once set. */
  readonly cueS: number | undefined;
  /** The track on the deck, as the player loaded it. */
  readonly track: TrackItem | undefined;
  /** The deck sounds. */
  readonly playing: boolean;
  readonly positionS: number;
  readonly durationS: number;
  /** What the middle of the record shows — see the header. */
  readonly face: RecordFace;
}

export interface DeckState {
  readonly decks: Readonly<Record<DeckSide, DeckSideState>>;
  readonly active: DeckSide;
  /** The active deck's record — see «Two decks, and the active one». */
  readonly size: RecordSize;
  /** The active deck's hand. */
  readonly hand: DeckHand;
  /** The active deck's tempo, in percent. */
  readonly pitch: number;
}

/** `percent` as the slider holds it: within reach, zero near zero, a tenth of a percent. */
export function pitchOf(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  const within = Math.max(-PITCH_RANGE, Math.min(PITCH_RANGE, percent));
  if (Math.abs(within) < PITCH_DETENT) return 0;
  // `+ 0` turns a rounded −0 into 0: the row would print «−0,0 %».
  return Math.round(within * 10) / 10 + 0;
}

/** What the phone holds under `key`, or `null` — a phone that refuses storage holds nothing. */
function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Remembered for this launch only.
  }
}

/**
 * A deck's own key, or — on a phone that has only the one deck's — the old one, copied to the
 * deck's key on the way, so it is read this once.
 */
function storedFor(own: string, legacy: string): string | null {
  const value = stored(own);
  if (value !== null) return value;
  const old = stored(legacy);
  if (old !== null) remember(own, old);
  return old;
}

function rememberedSize(side: DeckSide): RecordSize {
  return asRecordSize(storedFor(recordKey(side), LEGACY_RECORD_KEY)) ?? DEFAULT_RECORD;
}

/** The tempo the phone remembers — see the header; 0 for none, a broken one, a refusing phone. */
function rememberedPitch(side: DeckSide): number {
  const value = storedFor(pitchKey(side), LEGACY_PITCH_KEY);
  const number = Number(value ?? 0);
  return Number.isFinite(number) && Math.abs(number) <= PITCH_RANGE ? pitchOf(number) : 0;
}

function freshDeck(side: DeckSide): DeckSideState {
  return {
    size: rememberedSize(side),
    hand: 'none',
    pitch: rememberedPitch(side),
    keylock: false,
    cueS: undefined,
    track: undefined,
    playing: false,
    positionS: 0,
    durationS: 0,
    // The house label until a screen that shows the record draws one.
    face: 'jamaican',
  };
}

/** The state with the active deck's fields mirrored on top — see the header. */
function mirrored(decks: Record<DeckSide, DeckSideState>, active: DeckSide): DeckState {
  const own = decks[active];
  return { decks, active, size: own.size, hand: own.hand, pitch: own.pitch };
}

function launch(): DeckState {
  return mirrored({ A: freshDeck('A'), B: freshDeck('B') }, 'A');
}

export const useDeck = create<DeckState>(launch);

/** The decks `target` names: one, both, or — none named — the active one. */
function sidesOf(target: DeckTarget | undefined): readonly DeckSide[] {
  if (target === EVERY_DECK) return ['A', 'B'];
  return [target ?? useDeck.getState().active];
}

/** `side`, or the active deck when none is named. */
function sideOf(side: DeckSide | undefined): DeckSide {
  return side ?? useDeck.getState().active;
}

/** Changes `side`'s state, the mirrors with it. */
export function updateDeck(side: DeckSide, change: Partial<DeckSideState>): void {
  const state = useDeck.getState();
  const before = state.decks[side];
  const keys = Object.keys(change) as (keyof DeckSideState)[];
  if (keys.every((key) => before[key] === change[key])) return;
  useDeck.setState(mirrored({ ...state.decks, [side]: { ...before, ...change } }, state.active));
}

/** The player says which deck is active now. */
export function setActiveDeck(side: DeckSide): void {
  const state = useDeck.getState();
  if (state.active === side) return;
  useDeck.setState(mirrored({ ...state.decks }, side));
}

/** The pitch slider moved. Works under «уменьшение движения» too, and with a hand on the deck. */
export function setPitch(percent: number, target?: DeckTarget): void {
  const pitch = pitchOf(percent);
  for (const at of sidesOf(target)) {
    if (pitch === useDeck.getState().decks[at].pitch) continue;
    updateDeck(at, { pitch });
    remember(pitchKey(at), String(pitch));
  }
}

/** The speed of a record nobody touches, for a tempo: `1 + tempo`. */
export function tempoRate(pitch: number): number {
  return 1 + pitch / 100;
}

/** 33 or 45 pressed. Does nothing while a hand is on the deck. Touches nothing of the sound. */
export function chooseRecord(size: RecordSize, target?: DeckTarget): void {
  const sides = sidesOf(target);
  // The turntable's 33/45 is one key for both decks: a hand on either holds it.
  if (sides.some((at) => useDeck.getState().decks[at].hand !== 'none')) return;
  for (const at of sides) {
    const own = useDeck.getState().decks[at];
    if (own.hand !== 'none' || own.size === size) continue;
    updateDeck(at, { size });
    remember(recordKey(at), size);
  }
}

/** The record of `side` shows `face` now: drawn by «Сейчас играет» or the pult (`lib/labels.ts`). */
export function setFace(side: DeckSide, face: RecordFace): void {
  updateDeck(side, { face });
}

/** KEYLOCK pressed: the deck holds its tone at every speed, or lets it go again. */
export function toggleKeylock(side: DeckSide): void {
  updateDeck(side, { keylock: !useDeck.getState().decks[side].keylock });
}

/** A hand takes the deck, if it is free: `false` when another hand has it. */
export function takeDeck(hand: Exclude<DeckHand, 'none'>, side?: DeckSide): boolean {
  const at = sideOf(side);
  if (useDeck.getState().decks[at].hand !== 'none') return false;
  updateDeck(at, { hand });
  return true;
}

/** The hand that had the deck lets it go. */
export function leaveDeck(hand: Exclude<DeckHand, 'none'>, side?: DeckSide): void {
  const at = sideOf(side);
  if (useDeck.getState().decks[at].hand === hand) updateDeck(at, { hand: 'none' });
}

/** A launch again, for tests: records and tempos as the phone remembers them, no hand, deck A. */
export function resetDeck(): void {
  useDeck.setState(launch(), true);
}
